import type { Message, Part } from "@mux/sdk/v2/client"
import { mergeMessageTurnDiff } from "./turn-diff-message"
import { cmpMessage, isOutlinePart, mergeIncompleteAssistantMessages, mergeMessageParts, mergeStreamingPart, sortParts } from "./message-reducer"

/** Mutations observed after a snapshot request began; values remain in the canonical store. */
export function createSnapshotChanges() {
  return { messages: new Set<string>(), parts: new Map<string, Set<string>>() }
}
export type SnapshotChanges = ReturnType<typeof createSnapshotChanges>

export function reconcileTranscriptSnapshot(input: {
  live: readonly Message[]
  incoming: Message[]
  parts: Array<{ id: string; part: Part[] }>
  liveParts: (messageID: string) => Part[] | undefined
  changes: SnapshotChanges
  prepend?: boolean
  /** The machine replaced the transcript: no loaded message outside `incoming` is kept as older history. */
  replace?: boolean
  complete: boolean
}) {
  const liveByID = new Map(input.live.map((message) => [message.id, message]))
  const incoming = mergeIncompleteAssistantMessages([...input.live], input.incoming)
    .map((message) => mergeMessageTurnDiff(message, liveByID.get(message.id)))
  const messages = new Map(incoming.map((message) => [message.id, message]))
  const first = incoming[0]
  let retainedHistory = false
  for (const message of input.live) {
    if (input.prepend || (!input.replace && !input.complete && first && cmpMessage(message, first) < 0)) {
      if (!messages.has(message.id)) {
        messages.set(message.id, message)
        retainedHistory = true
      }
    }
  }
  for (const id of input.changes.messages) {
    const current = liveByID.get(id)
    if (current) messages.set(id, mergeMessageTurnDiff(current, messages.get(id)))
    else messages.delete(id)
  }
  // Loaded messages the snapshot deleted; their parts and metadata go with them.
  const removed = input.live.filter((message) => !messages.has(message.id)).map((message) => message.id)
  const parts = input.parts.filter((item) => messages.has(item.id)).map((item) => {
    const live = input.liveParts(item.id)
    const merged = new Map(mergeMessageParts(messages.get(item.id), live, item.part).map((part) => [part.id, part]))
    const liveParts = new Map(live?.map((part) => [part.id, part]))
    const outlined = [...merged.values()].some(isOutlinePart)
    for (const id of input.changes.parts.get(item.id) ?? []) {
      const current = liveParts.get(id)
      if (current) {
        const snapshot = merged.get(id)
        if (snapshot) merged.set(id, mergeStreamingPart(snapshot, current))
        // A part the outline folds stays folded, as a live update leaves it.
        else if (!outlined) merged.set(id, current)
      }
      else merged.delete(id)
    }
    return { id: item.id, part: sortParts([...merged.values()]) }
  })
  return { messages: [...messages.values()].sort(cmpMessage), parts, removed, retainedHistory }
}
